import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Card } from '@/components/ui/card';
import { useWizardStore } from '@/store/webinar';
import { WebinarStartStep } from '@/components/webinar/wizard/WebinarStartStep';
import { AgendamentoStep } from '@/components/webinar/wizard/steps/AgendamentoStep';
import { VideoStep } from '@/components/webinar/wizard/steps/VideoStep';
import { InscricaoStep } from '@/components/webinar/wizard/steps/InscricaoStep';
import { OfertaStep } from '@/components/webinar/wizard/steps/OfertaStep';
import { WizardHeader } from '@/components/webinar/wizard/WizardHeader';
import { WizardStepper } from '@/components/webinar/wizard/WizardStepper';
import { WizardFooter } from '@/components/webinar/wizard/WizardFooter';
import { WizardHelpPanel } from '@/components/webinar/wizard/WizardHelpPanel';
import { useWebinarAutosave } from '@/hooks/useWebinarAutosave';
import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function WebinarWizard() {
  useLanguage();
  const navigate = useNavigate();
  const { currentStep, steps = [], nextStep, prevStep, setCurrentStep, data } = useWizardStore();
  const stepsArr = Array.isArray(steps) ? steps : [];

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  
  // Autosave hook
  const { isSaving, lastSaved } = useWebinarAutosave(data);

  const handleSaveDraft = async () => {
    // Manual save trigger
    console.log('Saving draft manually...');
  };

  const handlePreview = () => {
    // Open preview in new tab
    if (data.slug) {
      window.open(`/w/${data.slug}`, '_blank');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WebinarStartStep />;
      case 2:
        return <AgendamentoStep />;
      case 3:
        return <InscricaoStep />;
      case 4:
        return <VideoStep />;
      case 5:
        return <OfertaStep />;
      case 6:
        return <div className="p-8 text-center text-muted-foreground">{translateInterfaceText("Step 6 - Em desenvolvimento")}</div>;
      case 7:
        return <div className="p-8 text-center text-muted-foreground">{translateInterfaceText("Step 7 - Em desenvolvimento")}</div>;
      case 8:
        return <div className="p-8 text-center text-muted-foreground">{translateInterfaceText("Step 8 - Em desenvolvimento")}</div>;
      case 9:
        return <div className="p-8 text-center text-muted-foreground">{translateInterfaceText("Step 9 - Em desenvolvimento")}</div>;
      case 10:
        return <div className="p-8 text-center text-muted-foreground">{translateInterfaceText("Step 10 - Em desenvolvimento")}</div>;
      default:
        return null;
    }
  };

  return (
    <Layout fixedSidebar>
      <div className="flex flex-col h-screen bg-background">
        {/* Header - Fixed */}
        <div className="flex-shrink-0">
          <WizardHeader
            currentStep={currentStep}
            totalSteps={stepsArr.length}
            onSaveDraft={handleSaveDraft}
            onPreview={data.slug ? handlePreview : undefined}
            isSaving={isSaving}
          />
        </div>

        {/* Stepper - Fixed */}
        <div className="flex-shrink-0">
          <WizardStepper
            steps={stepsArr}
            currentStep={currentStep}
            onStepClick={setCurrentStep}
          />
        </div>

        {/* Step Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-6 py-8">
            <Card className="max-w-4xl mx-auto">
              <div className="p-6">
                {/* Section Header with Help - Hide for Video and Offer Steps */}
                {currentStep !== 4 && currentStep !== 5 && (
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">
                        {stepsArr.find(s => s.id === currentStep)?.title || translateInterfaceText("Configuração")}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {translateInterfaceText("Preencha as informações abaixo para configurar esta etapa")}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsHelpOpen(true)}
                    >
                      <HelpCircle className="h-5 w-5 text-muted-foreground" />
                    </Button>
                  </div>
                )}

                {/* Step Content */}
                {renderStep()}
              </div>
            </Card>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0">
          <WizardFooter
            currentStep={currentStep}
            totalSteps={stepsArr.length}
            canGoBack={currentStep > 1}
            canGoNext={currentStep <= stepsArr.length}
            onBack={prevStep}
            onNext={nextStep}
            isSaving={isSaving}
            lastSaved={lastSaved}
          />
        </div>

        {/* Help Panel */}
        <WizardHelpPanel
          open={isHelpOpen}
          onOpenChange={setIsHelpOpen}
          step={currentStep}
        />
      </div>
    </Layout>
  );
}
